import type { ICCTVReportActivityObservationData } from '@/models/report'
import type { AxiosInstance } from 'axios'
import axiosRetry from 'axios-retry'

import axios, { isAxios401Error } from '@/axios'
import { objectToFormData } from '@/utils/helpers'

import type { ICCTVActivityObservationService } from './cctv-activity-observation.types'
/**
 * A class representing a CCTVActivityService API handler.
 */
export class CCTVActivityObservationService implements ICCTVActivityObservationService {
  private endpoint: string

  // CCTVActivityService constructor
  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/reports/cctv/'
  }

  // Method to fetch a list of reports from the API
  fetchCCTVActivityObservations(
    reportId: number,
    activityId: number
  ): Promise<ICCTVReportActivityObservationData[]> {
    return this.axios
      .get(`${this.endpoint}${reportId}/activities/${activityId}/observations/`)
      .then(({ data }) => data)
  }

  // Method to fetch a single report by their ID from the API
  fetchCCTVActivityObservation(
    reportId: number,
    activityId: number,
    id: number
  ): Promise<ICCTVReportActivityObservationData> {
    return this.axios
      .get(`${this.endpoint}${reportId}/activities/${activityId}/observations/${id}/`)
      .then(({ data }) => data)
  }

  // Method to create a new report on the API
  createCCTVActivityObservation(
    reportId: number,
    activityId: number,
    data: ICCTVReportActivityObservationData
  ): Promise<ICCTVReportActivityObservationData> {
    const formData = objectToFormData(data)
    return this.axios
      .post(`${this.endpoint}${reportId}/activities/${activityId}/observations/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // LF-2023-01-13: Hack to ensure file is set on token refresh (if required).
        // Required if 401 occurred while handling multipart/form-data
        'axios-retry': {
          retries: 1,
          retryCondition: isAxios401Error || axiosRetry.isSafeRequestError
        }
      })
      .then(({ data }) => data)
  }

  // Method to update an existing report on the API
  updateCCTVActivityObservation(
    reportId: number,
    activityId: number,
    data: ICCTVReportActivityObservationData
  ): Promise<ICCTVReportActivityObservationData> {
    const formData = objectToFormData(data)
    return this.axios
      .patch(
        `${this.endpoint}${reportId}/activities/${activityId}/observations/${data.id}/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // LF-2023-01-13: Hack to ensure file is set on token refresh (if required).
          // Required if 401 occurred while handling multipart/form-data
          'axios-retry': {
            retries: 1,
            retryCondition: isAxios401Error || axiosRetry.isSafeRequestError
          }
        }
      )
      .then(({ data }) => data)
  }

  // Method to delete a report by their ID from the API
  deleteCCTVActivityObservation(reportId: number, activityId: number, id: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${reportId}/activities/${activityId}/observations/${id}/`)
      .then(({ data }) => data)
  }
}

// Default export instantiation of the CCTVActivityObservationService
const activityReportService = new CCTVActivityObservationService(axios)
export default activityReportService
